import { GenerateTextProps } from '@editor/api/aiCopilot';
import { ActionOrigin, VectorizingSolver } from '@editor/api/generators';
import { TemplateResponse } from '@editor/api/templates';
import { AddGeneratedDesign } from '@editor/components/Canvas/handleEvents/addRemove/handleAddGeneratedDesign';
import { DuplicateEventValue } from '@editor/components/Canvas/handleEvents/design/handleDuplicate';
import { PreviewEventValue } from '@editor/components/Canvas/handleEvents/design/handlePreview';
import { RestoreLastSaveEventValue } from '@editor/components/Canvas/handleEvents/design/handleRestoreLastSave';
import { DraggableObject } from '@editor/components/Canvas/handleEvents/types';
import { makeDownloadEventParameters } from '@editor/components/DownloadPanel/utils';
import { StandardBoard } from '@editor/fabric/Canvas/types';
import { FontVariationAxis } from '@editor/fabric/shapes/RichText';
import { OutpaintingDetails } from '@editor/features/outpainting/fabric/outpaintable';
import { OutpaintingStatus } from '@editor/features/outpainting/types';
import { OnDropValue } from '@editor/hooks/useDragNDrop/types';
import { HistoryState } from '@editor/services/history';
import { DownloadFormat } from '@editor/stores/downloadStore/types';
import type {
  DecorationPresetParameters,
  FillPresetParameters,
  LibraryAsset,
  LibraryPreset,
  PhotoFilterPresetParameters,
  ShadowPresetParameters,
  TextEffectPresetParameters,
  TextPresetParameters,
} from '@editor/stores/librariesStore';
import { ColorPalettePresetParameters } from '@editor/stores/librariesStore/types/presets';
import { ActiveTool } from '@editor/stores/menuStore/types';
import {
  Align,
  ArtboardOptions,
  ArtboardSizeOptions,
  BlendMode,
  ColorPaletteItemLinkedObject,
  Design,
  DesignState,
  DesignStateMultiBoard,
  DesignStateMultiBoardForInit,
  MinSerializedObject,
  Post,
  PrintBleed,
  PrintMargins,
  SerializedObject,
  Snapshot,
  Unit,
} from '@editor/types';
import type * as eventTypes from '@editor/types/events';
import {
  CopyActiveObjectsAsStateEvent,
  CopyActiveObjectStyleEvent,
  ElementType,
  PasteActiveObjectStyleEvent,
  PasteFromClipboardEvent,
  PasteFromFileDropEvent,
  PreviewingParams,
} from '@editor/types/events';
import { ShapeBooleanOperation } from '@editor/types/pathfinder';
import { Color } from '@editor/utils/color';
import { StructureObject } from '@editor/utils/groupStructure';
import { TLatencyActionCompleteCallback } from '@editor/utils/performance/latencyTracker';
import { ShareType } from '@editor/utils/share';
import { SubscriptionPlan } from '@repo/domain/user';
import { fabric } from 'fabric';
import { EventDataNode } from 'rc-tree/lib/interface';

import { ColorPaletteTrigger } from './analytics/colorPalette';
import { RoundTrigger } from './analytics/illustration';
import { PrintBleedTrigger } from './analytics/printBleed';
import { PrintMarginTrigger } from './analytics/printMargins';
import { ColorOptions } from './styling/fill';
import { SerialisedPhotoFilter } from './styling/photoFilter';
import { CharacterStyles, Glyph } from './styling/text';
import { TransformOptions } from './styling/transformation';

// This is what you may call action types in redux

// canvas events
export const CANVAS_PREFIX = 'canvas';
export const CANVAS_STATE = `${CANVAS_PREFIX}:state` as const;
export const CANVAS_STATE_CHANGED = `${CANVAS_PREFIX}:stateChanged` as const;
export const CANVAS_SELECTION = `${CANVAS_PREFIX}:selection` as const;
export const CANVAS_ACTIVE_OBJECT = `${CANVAS_PREFIX}:activeObject` as const;
export const CANVAS_IS_LOADING = `${CANVAS_PREFIX}:isLoading` as const;
export const CANVAS_LOAD_PROJECT = `${CANVAS_PREFIX}:loadProject` as const;

// Add new element events
export const ADD_PREFIX = 'add';
export const ADD_TEXT = `${ADD_PREFIX}:text` as const;
export const ADD_ILLUSTRATION = `${ADD_PREFIX}:illustration` as const;
export const ADD_ILLUSTRATION_COLLECTION =
  `${ADD_PREFIX}:illustrationCollection` as const;
export const ADD_BASIC_SHAPE = `${ADD_PREFIX}:basicShape` as const;
export const ADD_TEXTURE = `${ADD_PREFIX}:texture` as const;
export const ADD_BACKGROUND_IMAGE = `${ADD_PREFIX}:backgroundImage` as const;
export const ADD_TEMPLATE = `${ADD_PREFIX}:template` as const;
export const ADD_TEMPLATE_FROM_URL = `${ADD_PREFIX}:templateFromUrl` as const;
export const ADD_TEXT_LAYOUT = `${ADD_PREFIX}:textLayout` as const;
export const ADD_PHOTO = `${ADD_PREFIX}:photo` as const;
export const ADD_MASK = `${ADD_PREFIX}:mask` as const;
export const ADD_MOCKUP_TEMPLATE = `${ADD_PREFIX}:mockupTemplate` as const;
export const ADD_GENERATED_DESIGN = `${ADD_PREFIX}:generatedDesign` as const;
export const ADD_DRAWABLE_SHAPE = `${ADD_PREFIX}:drawableShape` as const;

// Pen tool
export const PENTOOL_PREFIX = 'pentool' as const;
export const PENTOOL_ACTIVATED = `${PENTOOL_PREFIX}:activated` as const;
export const PENTOOL_PATH_DRAWN = `${PENTOOL_PREFIX}:pathDrawn` as const;
export const PENTOOL_PATH_CONTINUED =
  `${PENTOOL_PREFIX}:pathContinued` as const;

// Vector editing
export const VECTOR_EDITING_PREFIX = 'vectorEditing' as const;
export const VECTOR_EDITING_POINT_MODIFIED =
  `${VECTOR_EDITING_PREFIX}:pointModified` as const;

// Edit elements
export const EDIT_PREFIX = 'edit';
export const EDIT_TEXT = `${EDIT_PREFIX}:text` as const;
export const EDIT_SHAPE = `${EDIT_PREFIX}:shape` as const;
export const EDIT_TEXTURE = `${EDIT_PREFIX}:texture` as const;
export const EDIT_BACKGROUND_IMAGE = `${EDIT_PREFIX}:backgroundImage` as const;
export const EDIT_DRAWABLE_SHAPE = `${EDIT_PREFIX}:drawableShape` as const;

export const PREVIEW_BACKGROUND_BLEND_MODE =
  `${EDIT_PREFIX}:previewBackgroundBlendMode` as const;
export const PREVIEW_TEXTURE_BLEND_MODE =
  `${EDIT_PREFIX}:previewTextBlendMode` as const;

// Replace elements
export const REPLACE_ELEMENT = `replace:element`;
export const REPLACE_TEXTS_BULK = `replace:text`;

// Active object events
export const ACTIVE_PREFIX = 'active';
const STYLE_TAG = 'style'; //Events that change styles should have this property
export const ACTIVE_DELETE = `${ACTIVE_PREFIX}:delete` as const;
export const ACTIVE_OPACITY = `${ACTIVE_PREFIX}:opacity` as const;
export const ACTIVE_MOVE = `${ACTIVE_PREFIX}:move` as const;
export const ACTIVE_MOVE_BY = `${ACTIVE_PREFIX}:moveBy` as const;
export const ACTIVE_MOVED = `${ACTIVE_PREFIX}:moved` as const;
export const ACTIVE_COPY_OBJECTS_AS_STATE =
  `${ACTIVE_PREFIX}:copyObjectsAsState` as const;
export const ACTIVE_PASTE_FROM_CLIPBOARD =
  `${ACTIVE_PREFIX}:pasteFromClipboard` as const;
export const ACTIVE_PASTE_FROM_EVENT =
  `${ACTIVE_PREFIX}:pasteFromEvent` as const;
export const ACTIVE_PASTE_FROM_DROP = `${ACTIVE_PREFIX}:pasteFromDrop` as const;
export const ACTIVE_COPY_STYLE = `${ACTIVE_PREFIX}:copyStyle` as const;
export const ACTIVE_COPY_STYLE_FILL = `${ACTIVE_PREFIX}:copyStyleFill` as const;
export const ACTIVE_COPY_STYLE_FONT = `${ACTIVE_PREFIX}:copyStyleFont` as const;
export const ACTIVE_COPY_STYLE_TEXT_DECORATION =
  `${ACTIVE_PREFIX}:copyStyleTextDecoration` as const;
export const ACTIVE_COPY_STYLE_SHADOW =
  `${ACTIVE_PREFIX}:copyStyleShadow` as const;
export const ACTIVE_COPY_STYLE_PHOTO_FILTERS =
  `${ACTIVE_PREFIX}:copyStylePhotoFilters` as const;
export const ACTIVE_PASTE_STYLE = `${ACTIVE_PREFIX}:pasteStyle` as const;
export const ACTIVE_DUPLICATE = `${ACTIVE_PREFIX}:duplicate` as const;
export const ACTIVE_NEW_ARTBOARD = `${ACTIVE_PREFIX}:newArtboard` as const;
export const ACTIVE_CUT = `${ACTIVE_PREFIX}:cut` as const;
export const ACTIVE_REFLECT_HORIZONTAL =
  `${ACTIVE_PREFIX}:reflectHorizontal` as const;
export const ACTIVE_REFLECT_VERTICAL =
  `${ACTIVE_PREFIX}:reflectVertical` as const;
export const ACTIVE_CROP = `${ACTIVE_PREFIX}:crop` as const;
// Outpainting events
export const ACTIVE_OUTPAINT = `${ACTIVE_PREFIX}:outpaint` as const;
export const ACTIVE_OUTPAINT_DETAILS =
  `${ACTIVE_PREFIX}:outpaintDetails` as const;
export const ACTIVE_OUTPAINT_APPLY_PRESET =
  `${ACTIVE_PREFIX}:outpaintControlPointsUpdate` as const;
export const ACTIVE_OUTPAINT_REPLACE =
  `${ACTIVE_PREFIX}:outpaintReplace` as const;
export const ACTIVE_OUTPAINT_EXIT = `${ACTIVE_PREFIX}:outpaintExit` as const;
export const ACTIVE_OUTPAINT_CHANGE_STATUS =
  `${ACTIVE_PREFIX}:outpaintChangeStatus` as const;
//
export const ACTIVE_SAVE_AS_STYLE = `${ACTIVE_PREFIX}:saveAsStyle` as const;
export const ACTIVE_USE_AS_STYLE_SWITCH_IMAGE =
  `${ACTIVE_PREFIX}:useAsStyleSwitchImage` as const;
export const ACTIVE_REPLACE_ELEMENT =
  `${ACTIVE_PREFIX}:replaceElement` as const;
export const ACTIVE_ELEMENT_OPACITY_ANIMATE_START =
  `${ACTIVE_PREFIX}:elementOpacityAnimateStart` as const;
export const ACTIVE_ELEMENT_OPACITY_ANIMATE_STOP =
  `${ACTIVE_PREFIX}:elementOpacityAnimateStop` as const;
export const ACTIVE_EXIT_CROP = `${ACTIVE_PREFIX}:exitCrop` as const;
export const ACTIVE_DOWNLOAD = `${ACTIVE_PREFIX}:download` as const;
export const ACTIVE_GROUP = `${ACTIVE_PREFIX}:group` as const;
export const ACTIVE_LOCK = `${ACTIVE_PREFIX}:lock` as const;
export const ACTIVE_ALIGN = `${ACTIVE_PREFIX}:align` as const;
export const ACTIVE_GLYPH = `${ACTIVE_PREFIX}:glyph` as const;
export const ACTIVE_OVERLAY_MODE = `${ACTIVE_PREFIX}:overlayMode` as const;
export const PREVIEW_OVERLAY_MODE =
  `${ACTIVE_PREFIX}:previewOverlayMode` as const;
export const ACTIVE_COLOR = `${ACTIVE_PREFIX}:${STYLE_TAG}:color` as const;
export const ACTIVE_COLOR_HIDE =
  `${ACTIVE_PREFIX}:${STYLE_TAG}:colorHide` as const;
export const ACTIVE_STROKE_WIDTH =
  `${ACTIVE_PREFIX}:${STYLE_TAG}:strokeWidth` as const;
export const ACTIVE_FONTSIZE =
  `${ACTIVE_PREFIX}:${STYLE_TAG}:fontSize` as const;
export const ACTIVE_LETTERSPACING =
  `${ACTIVE_PREFIX}:${STYLE_TAG}:letterSpacing` as const;
export const ACTIVE_FONT_STYLE_ATTRIBUTES =
  `${ACTIVE_PREFIX}:${STYLE_TAG}:fontStyleAttributes` as const;
export const ACTIVE_FONTFAMILY =
  `${ACTIVE_PREFIX}:${STYLE_TAG}:fontFamily` as const;
export const ACTIVE_TEXTALIGNMENT =
  `${ACTIVE_PREFIX}:${STYLE_TAG}:textAlignment` as const;
export const ACTIVE_UPPERCASE =
  `${ACTIVE_PREFIX}:${STYLE_TAG}:uppercase` as const;
export const ACTIVE_LIGATURES =
  `${ACTIVE_PREFIX}:${STYLE_TAG}:ligatures` as const;
export const ACTIVE_LINEHEIGHT =
  `${ACTIVE_PREFIX}:${STYLE_TAG}:lineHeight` as const;
export const ACTIVE_VARIATION =
  `${ACTIVE_PREFIX}:${STYLE_TAG}:variation` as const;
export const ACTIVE_SHADOW = `${ACTIVE_PREFIX}:${STYLE_TAG}:shadow` as const;
export const ACTIVE_TRANSFORM =
  `${ACTIVE_PREFIX}:${STYLE_TAG}:transform` as const;
export const ACTIVE_TRANSFORM_CURVE =
  `${ACTIVE_PREFIX}:${STYLE_TAG}:transformCurve` as const;
export const ACTIVE_DECORATION =
  `${ACTIVE_PREFIX}:${STYLE_TAG}:decoration` as const;
export const ACTIVE_CLIPPING_MASK = `${ACTIVE_PREFIX}:clippingMask` as const;
export const ACTIVE_EDIT_MODE = `${ACTIVE_PREFIX}:editMode` as const;
export const ACTIVE_TIDY = `${ACTIVE_PREFIX}:tidy` as const;
export const ACTIVE_REMOVE_BG = `${ACTIVE_PREFIX}:removeBackground` as const;
export const ACTIVE_REMOVE_BG_BULK =
  `${ACTIVE_PREFIX}:removeBackgroundBulk` as const;
export const ACTIVE_VECTORIZE = `${ACTIVE_PREFIX}:vectorize` as const;
export const ACTIVE_VECTORIZE_BULK = `${ACTIVE_PREFIX}:vectorizeBulk` as const;
export const ACTIVE_ENTER_KEY = `${ACTIVE_PREFIX}:enterKey` as const;
export const ACTIVE_FILTER = `${ACTIVE_PREFIX}:filter` as const;
export const ACTIVE_FILTER_RESET = `${ACTIVE_PREFIX}:filterReset` as const;
export const ACTIVE_COLOR_PREVIEW = `${ACTIVE_PREFIX}:previewColor` as const;
export const ACTIVE_AS_BACKGROUND_IMAGE =
  `${ACTIVE_PREFIX}:asBackgroundImage` as const;
export const ACTIVE_AS_TEXTURE = `${ACTIVE_PREFIX}:asTexture` as const;
export const ACTIVE_UPSCALE = `${ACTIVE_PREFIX}:upscale` as const;
export const ACTIVE_UPSCALE_BULK = `${ACTIVE_PREFIX}:upscaleBulk` as const;
export const ACTIVE_RESIZE = `${ACTIVE_PREFIX}:resize` as const;
export const ACTIVE_TOGGLE_ERASE_MODE = `${ACTIVE_PREFIX}:eraseMode` as const;
export const ACTIVE_RASTERIZE_AND_ERASE =
  `${ACTIVE_PREFIX}:rasterizeAndErase` as const;
export const ACTIVE_SET_ERASE_STROKE_WIDTH =
  `${ACTIVE_PREFIX}:setEraseStrokeWidth` as const;
export const ACTIVE_INCREASE_ERASE_STROKE_WIDTH =
  `${ACTIVE_PREFIX}:increaseEraseStrokeWidth` as const;
export const ACTIVE_DECREASE_ERASE_STROKE_WIDTH =
  `${ACTIVE_PREFIX}:decreaseEraseStrokeWidth` as const;
export const ACTIVE_SCALING = `${ACTIVE_PREFIX}:scaling` as const;
export const ACTIVE_SCALED = `${ACTIVE_PREFIX}:scaled` as const;
export const ACTIVE_REPLACE_TEXT = `${ACTIVE_PREFIX}:replaceText` as const;
export const ACTIVE_EXIT_ERASE = `${ACTIVE_PREFIX}:exitErase` as const;
export const ACTIVE_SET_CORNER_RADIUS_SQUARE =
  `${ACTIVE_PREFIX}:setCornerRadiusSquare` as const;
export const ACTIVE_SET_SELECTED_CORNER_RADIUS =
  `${ACTIVE_PREFIX}:setSelectedCornerRadius` as const;
export const ACTIVE_TOGGLE_SCALING_CORNERS =
  `${ACTIVE_PREFIX}:toggleScalingCorners` as const;
export const ACTIVE_APPLY_BOOLEAN_OPERATIONS =
  `${ACTIVE_PREFIX}:applyBooleanOperations` as const;
export const ACTIVE_VECTORIZE_TEXT = `${ACTIVE_PREFIX}:vectorizeText` as const;
export const ACTIVE_UPDATE_SEGMENTS =
  `${ACTIVE_PREFIX}:updateSegments` as const;

// Layer events
export const LAYER_PREFIX = 'layer';
export const LAYER_LOCKED = `${LAYER_PREFIX}:locked` as const;
export const LAYER_HIDDEN = `${LAYER_PREFIX}:hidden` as const;
export const LAYER_MOVE = `${LAYER_PREFIX}:move` as const;
export const LAYER_SELECTION = `${LAYER_PREFIX}:selection` as const;
export const LAYER_SEND_TO = `${LAYER_PREFIX}:sendTo` as const;
export const LAYER_RENAME = `${LAYER_PREFIX}:rename` as const;
export const LAYER_EDITING_NAME = `${LAYER_PREFIX}:editingName` as const;
export const LAYER_HIGHLIGHT = `${LAYER_PREFIX}:highlight` as const;

// History events
export const HISTORY_PREFIX = 'history';
export const HISTORY_CHANGES = `${HISTORY_PREFIX}:changes` as const;
export const UNTRACKED_HISTORY_CHANGES =
  `${HISTORY_PREFIX}:untracked_changes` as const;
export const HISTORY_UNDO = `${HISTORY_PREFIX}:undo` as const;
export const HISTORY_REDO = `${HISTORY_PREFIX}:redo` as const;
export const HISTORY_RESET = `${HISTORY_PREFIX}:reset` as const;

// Design events
export const DESIGN_PREFIX = 'design';
export const DESIGN_RENAME = `${DESIGN_PREFIX}:rename` as const;
export const DESIGN_SAVE = `${DESIGN_PREFIX}:save` as const;
export const DESIGN_DELETE = `${DESIGN_PREFIX}:deleteDesign` as const;
export const DESIGN_DUPLICATE = `${DESIGN_PREFIX}:duplicateDesign` as const;
export const DESIGN_RESTORE_LAST_SAVE =
  `${DESIGN_PREFIX}:restoreLastSave` as const;
export const DESIGN_SHARE = `${DESIGN_PREFIX}:share` as const;
export const DESIGN_PREVIEW = `${DESIGN_PREFIX}:setPreview` as const;

// Miscellaneous events
const MISC_PREFIX = 'misc';
export const UPDATE_PROJECT_UNIT = `${MISC_PREFIX}:updateProjectUnit` as const;
export const UPDATE_PROJECT_PRINT_BLEED =
  `${MISC_PREFIX}:updateProjectPrintBleed` as const;
export const OPEN_DOWNLOAD_PANEL = `${MISC_PREFIX}:openDownloadPanel` as const;
export const DOWNLOAD = `${MISC_PREFIX}:download` as const;
export const DOWNLOAD_SELECTION = `${MISC_PREFIX}:downloadSelection` as const;
export const DOWNLOAD_WITH_REFACTORING =
  `${MISC_PREFIX}:downloadWithRefactoring` as const;
export const SET_STATE = `${MISC_PREFIX}:setState` as const;
export const SELECT_ALL = `${MISC_PREFIX}:selectAll` as const;
export const SET_BACKGROUND = `${MISC_PREFIX}:setBackground` as const;
export const SET_COLOR_PALETTE = `${MISC_PREFIX}:setColorPalette` as const;
export const SET_COLOR_PALETTE_PREVIEW =
  `${MISC_PREFIX}:setColorPalettePreview` as const;
export const SET_COLOR_PALETTE_PRESET =
  `${MISC_PREFIX}:setColorPalettePreset` as const;
export const SET_COLOR_PALETTE_PRESET_PREVIEW =
  `${MISC_PREFIX}:setColorPalettePresetPreview` as const;
export const STOP_COLOR_PREVIEW =
  `${MISC_PREFIX}:removeColorPalettePresetPreview` as const;
export const GRID_SET = `${MISC_PREFIX}:gridSet` as const;
export const GRID_SHOW = `${MISC_PREFIX}:gridShow` as const;
export const RULER_SHOW = `${MISC_PREFIX}:rulerShow` as const;
export const PRINT_MARGINS_SHOW = `${MISC_PREFIX}:printMarginsShow` as const;
export const ZOOM = `${MISC_PREFIX}:zoom` as const;
export const ZOOM_TO = `${MISC_PREFIX}:zoomTo` as const;
export const ZOOM_STEP = `${MISC_PREFIX}:zoomStep` as const;
export const ZOOM_CENTER = `${MISC_PREFIX}:zoomCenter` as const;
export const ENABLE_SNAPPING = `${MISC_PREFIX}:enableSnapping` as const;
export const ENABLE_ZOOM_ON_WHEEL = `${MISC_PREFIX}:enableZoomOnWheel` as const;
export const ENABLE_DRAGGING = `${MISC_PREFIX}:enableDragging` as const;
export const RESIZE = `${MISC_PREFIX}:resize` as const;
export const SOCIAL_SHARE = `${MISC_PREFIX}:socialShare` as const;
export const COLOR_PICKING = `${MISC_PREFIX}:colorPicking` as const;
export const PICK_COLOR = `${MISC_PREFIX}:pickColor` as const;
export const DELETE_TEXTURE = `${MISC_PREFIX}:deleteTexture` as const;
export const DELETE_UPLOAD = `${MISC_PREFIX}:deleteUpload` as const;
export const DESELECT_OBJECTS = `${MISC_PREFIX}:deselectObjects` as const;
export const SELECT_ARTBOARD = `${MISC_PREFIX}:selectArtboard` as const;
export const ACTIVE_TRANSFORM_BUTTON =
  `${MISC_PREFIX}:transformButton` as const;
export const POST_SUBMIT = `${MISC_PREFIX}:postSubmit` as const;
export const OPEN_ARTBOARD_SIZE_MODAL =
  `${MISC_PREFIX}:openArtboardSizeModal` as const;
export const RELEASE_TEXTURE = `${MISC_PREFIX}:releaseTexture` as const;
export const OVERLAY_CLIP = `${MISC_PREFIX}:overlayClip` as const;
export const START_DRAGGING_ELEMENT =
  `${MISC_PREFIX}:startDraggingElement` as const;
export const DRAGGING_ELEMENT = `${MISC_PREFIX}:draggingElement` as const;
export const STOP_DRAGGING_ELEMENT =
  `${MISC_PREFIX}:stopDraggingElement` as const;
export const DELETE_BACKGROUND_IMAGE =
  `${MISC_PREFIX}:deleteBackgroundImage` as const;
export const RELEASE_BACKGROUND_IMAGE =
  `${MISC_PREFIX}:releaseBackgroundImage` as const;
export const REMOVE_OBJECTS = `${MISC_PREFIX}:removeObjects` as const;
export const ARTBOARD_CLIP = `${MISC_PREFIX}:artboardClip` as const;
export const ARTBOARD_PRINT_MARGIN =
  `${MISC_PREFIX}:artboardPrintMargin` as const;
export const TOGGLE_AI_COPILOT = `${MISC_PREFIX}:toggleAiCopilot` as const;
export const COPY_AS_PNG = `${MISC_PREFIX}:copyAsPNG` as const;
export const COPY_LINK_TO_SELECTION =
  `${MISC_PREFIX}:copyLinkToSelection` as const;
export const SET_ACTIVE_TOOL = `${MISC_PREFIX}:setActiveTool` as const;

// Debug events
export const DEV_PREFIX = 'development';
export const DEV_SAVE_TEXT_LAYOUT = `${DEV_PREFIX}:saveTextLayout` as const;
export const DEV_SAVE_MASTER_LAYOUT = `${DEV_PREFIX}:saveMasterLayout` as const;
export const DEV_ADJUST_TEXT = `${DEV_PREFIX}:adjustText` as const;
export const DEV_REPLICATE_TEXT_IN_ALL_FONTS =
  `${DEV_PREFIX}:replicateTextInAllFonts` as const;

// MTC events
const MTC_PREFIX = 'mtc';
export const MTC_SAVE = `${MTC_PREFIX}:save` as const;
export const MTC_UPDATE = `${MTC_PREFIX}:update` as const;
export const MTC_DELETE_WRAP_GRID = `${MTC_PREFIX}:deleteWarpGrid` as const;
export const MTC_CREATE_WRAP_GRID = `${MTC_PREFIX}:createWarpGrid` as const;

// Mockup events
const MOCKUP_PREFIX = 'mockup';
export const MOCKUP_SAVE_TO_UPLOADS = `${MOCKUP_PREFIX}:saveToUploads` as const;

// Main
const MAIN_PREFIX = 'main';
export const MAIN_SELECT = `${MAIN_PREFIX}:select` as const;

// board events
export const BOARD_PREFIX = 'board';
export const PREPARE_ARTBOARD_SELECTION =
  `${BOARD_PREFIX}:prepareArtboardSelection` as const;
export const ARTBOARD_COPY = `${BOARD_PREFIX}:artboardDuplicate` as const;
export const UPDATE_ARTBOARD_DEFAULT_TITLE =
  `${BOARD_PREFIX}:updateArtboardTitle` as const;
export const PREPARE_ARTBOARD_PRINT_MARGINS =
  `${BOARD_PREFIX}:prepareArtboardPrintMargins` as const;

// external events
export const DOWNLOAD_SAP = 'download:sap';

// preset events
export const PRESET_PREFIX = 'preset';
export const PRESET_LINK = `${PRESET_PREFIX}:link` as const;
export const PRESET_UNLINK = `${PRESET_PREFIX}:unlink` as const;
export const PRESET_LINK_VIA_COLOR_PALETTE =
  `${PRESET_PREFIX}:linkViaColorPalette` as const;
export const PRESET_UNLINK_VIA_SELECTION =
  `${PRESET_PREFIX}:unlinkViaSelection` as const;
export const PRESETS_PRE_UPDATE = `${PRESET_PREFIX}:preUpdate` as const;
export const PRESETS_CREATE = `${PRESET_PREFIX}:create` as const; // intentionally a different event to update because we don't want undo/redo on this
export const PRESETS_UPDATE = `${PRESET_PREFIX}:batchUpdate` as const;
export const PRESETS_REMOVE = `${PRESET_PREFIX}:batchRemove` as const;
export const PRESETS_PRE_REMOVE_APPLY =
  `${PRESET_PREFIX}:removePreUpdate` as const; // used to update yjs _before_ the rerender / dispatch from react
export const ADD_TEXT_OR_LINK_PRESET_STYLE =
  `${PRESET_PREFIX}:addTextORLinkPresetStyle` as const;

// focus mode events
export const FOCUS_MODE_PREFIX = 'focusMode';
export const FOCUS_MODE_ASCEND = `${FOCUS_MODE_PREFIX}:ascend` as const;
export const FOCUS_MODE_SUBMIT_CHANGES =
  `${FOCUS_MODE_PREFIX}:submitChanges` as const;

// assets events
export const ASSET_PREFIX = 'asset';
export const ASSET_ADD = `${ASSET_PREFIX}:add` as const;
export const ASSET_EDIT = `${ASSET_PREFIX}:edit` as const;
export const ASSET_CREATE = `${ASSET_PREFIX}:create` as const;
export const ASSET_CREATE_FROM_GROUP =
  `${ASSET_PREFIX}:createFromGroup` as const;
export const ASSET_CREATE_EMPTY = `${ASSET_PREFIX}:createEmpty` as const;
export const ASSET_RENAMED = `${ASSET_PREFIX}:renamed` as const;
export const ASSET_DETACH = `${ASSET_PREFIX}:detach` as const;
export const ASSETS_UPDATED = `${ASSET_PREFIX}:updated` as const;

export const COLOR_PAL_PREFIX = 'colorPal';
export const COLOR_PAL_GENERATE_COLOR_PALETTE =
  `${COLOR_PAL_PREFIX}:generateColorPalette` as const;
export const COLOR_PAL_DELETE_COLOR_PALETTE =
  `${COLOR_PAL_PREFIX}:delete` as const;

interface BlendModePayload {
  globalCompositeOperation: string;
  callback?: () => void;
  fallbackCallback?: TLatencyActionCompleteCallback;
}

export type ActiveArtboardEventValue = {
  callback?: () => void;
  source: 'CONTEXTUAL_TASKBAR' | 'RIGHT_CLICK_MENU';
};

export interface ResizePayload {
  /** Updated dimensions */
  updated: { width: number; height: number };
  /** Whether the aspect ratio is locked */
  lockAspectRatio: boolean;
  /** The pivot origin to perform resize operations around */
  pivotOrigin: PivotOrigin;
}

export enum PivotOrientation {
  CENTER = 'center',
  LEFT = 'left',
  RIGHT = 'right',
  TOP = 'top',
  BOTTOM = 'bottom',
}

export type PivotOrigin = {
  x: PivotOrientation.LEFT | PivotOrientation.CENTER | PivotOrientation.RIGHT;
  y: PivotOrientation.TOP | PivotOrientation.CENTER | PivotOrientation.BOTTOM;
};

export enum MoveType {
  Front = 'front',
  Forward = 'forward',
  Back = 'back',
  Backward = 'backward',
}

export interface EventShapes {
  [ACTIVE_SET_CORNER_RADIUS_SQUARE]: {
    radius: number;
    corner: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
    isChanging?: boolean;
    shouldTrackAnalytics?: boolean;
    onComplete?: () => void;
  };
  [ACTIVE_TOGGLE_SCALING_CORNERS]: { value: boolean };
  [ACTIVE_SET_SELECTED_CORNER_RADIUS]: {
    shouldTrackAnalytics?: boolean;
    trigger: RoundTrigger;
    radius: number;
    isChanging?: boolean;
    onComplete?: () => void;
  };
  [ACTIVE_VECTORIZE_TEXT]: undefined;
  [ACTIVE_APPLY_BOOLEAN_OPERATIONS]: {
    operation: ShapeBooleanOperation;
  };
  [ACTIVE_COLOR]: eventTypes.ActiveColorEvent;
  [START_DRAGGING_ELEMENT]: DraggableObject;
  [ADD_ILLUSTRATION]: {
    dropX?: number | undefined;
    dropY?: number | undefined;
    distanceToTopCorner?:
      | {
          x: number;
          y: number;
        }
      | undefined;
    imgWidth?: number | undefined;
    imgHeight?: number | undefined;
    objectName: string;
    skipPopularityTracking?: boolean;
    isGeneratedByAi?: boolean;
    relativeSize?: number;
    id?: string | number;
    src?: string;
    maskId?: string;
    callback?: (target: fabric.Object) => void | Promise<void>;
    skipObjectPreviews?: boolean;
    skipSelection?: boolean;
    forceFloating?: boolean;
    searchQuery?: string;
  };
  [ADD_ILLUSTRATION_COLLECTION]: {
    illustrations: {
      objectName: string;
      skipPopularityTracking: boolean;
      isGeneratedByAi: boolean;
    }[];
    placeInRowsOf?: number;
  };
  [ACTIVE_EXIT_CROP]: undefined;
  [ACTIVE_OUTPAINT_EXIT]: undefined;
  [STOP_COLOR_PREVIEW]: undefined;
  [ACTIVE_EDIT_MODE]: undefined;
  [ACTIVE_EXIT_ERASE]: undefined;
  [ACTIVE_ENTER_KEY]: undefined;
  [OPEN_ARTBOARD_SIZE_MODAL]: undefined;
  [ACTIVE_TRANSFORM_BUTTON]: undefined;
  [DEV_SAVE_TEXT_LAYOUT]: undefined;
  [ACTIVE_TOGGLE_ERASE_MODE]: {
    trigger?: string;
  };
  [ACTIVE_RASTERIZE_AND_ERASE]: undefined;
  [MTC_DELETE_WRAP_GRID]: undefined;
  [ACTIVE_FILTER_RESET]: undefined;
  [ACTIVE_DELETE]: undefined;
  [STOP_DRAGGING_ELEMENT]: undefined;
  [ACTIVE_TIDY]: { callback?: () => void };
  [RELEASE_BACKGROUND_IMAGE]: undefined;
  [DELETE_BACKGROUND_IMAGE]: undefined;
  [RELEASE_TEXTURE]: undefined;
  [DELETE_TEXTURE]: undefined;
  [ACTIVE_REMOVE_BG]:
    | { actionOrigin?: ActionOrigin; callback?: () => void }
    | undefined;
  [ACTIVE_REMOVE_BG_BULK]:
    | { actionOrigin?: ActionOrigin; callback?: () => void }
    | undefined;
  [ACTIVE_UPSCALE]:
    | { actionOrigin?: ActionOrigin; callback?: () => void }
    | undefined;
  [ACTIVE_UPSCALE_BULK]:
    | { actionOrigin?: ActionOrigin; callback?: () => void }
    | undefined;
  [ACTIVE_CLIPPING_MASK]: undefined;
  [DOWNLOAD]: ReturnType<typeof makeDownloadEventParameters>;
  [MOCKUP_SAVE_TO_UPLOADS]: undefined;
  [EDIT_SHAPE]: boolean;
  [TOGGLE_AI_COPILOT]: {
    trigger?: string;
  };
  [ACTIVE_DUPLICATE]: undefined;
  [ACTIVE_CROP]: undefined;
  [ACTIVE_OUTPAINT]: undefined;
  [ACTIVE_OUTPAINT_DETAILS]: {
    onSuccess: (details: OutpaintingDetails) => Promise<void>;
  };
  [ACTIVE_OUTPAINT_APPLY_PRESET]: { newAspectRatio: [number, number] };
  [ACTIVE_OUTPAINT_REPLACE]: {
    objectName: string;
  };
  [ACTIVE_OUTPAINT_CHANGE_STATUS]: OutpaintingStatus;
  [DESELECT_OBJECTS]: { colors: Color[] };
  [ZOOM_CENTER]: { target: 'artboard' | 'artboards' | 'content' | 'selection' };
  [CANVAS_LOAD_PROJECT]: { id: string };
  [CANVAS_STATE_CHANGED]: eventTypes.CanvasStateChangedEvent;
  [CANVAS_STATE]: eventTypes.CanvasStateEvent;
  [CANVAS_SELECTION]: eventTypes.CanvasSelectionEvent;
  [CANVAS_ACTIVE_OBJECT]: eventTypes.CanvasActiveObjectEvent;
  [CANVAS_IS_LOADING]: boolean;
  [ADD_TEXT]:
    | (ColorPalettePresetParameters & { cb?: () => void })
    | (DecorationPresetParameters & { cb?: () => void })
    | (TextEffectPresetParameters & { cb?: () => void })
    | (TextPresetParameters & { cb?: () => void })
    | (ShadowPresetParameters & {
        cb?: () => void;
      })
    | (PhotoFilterPresetParameters & { cb?: () => void })
    | (FillPresetParameters & { cb?: () => void })
    | undefined
    | {
        width?: number;
        deselect?: boolean;
        text?: string;
        relativeSize?: number;
        fill?: Color;
        fontFamily?: string;
        skipEditModeOnCreatio?: boolean;
        skipEditModeOnCreation?: boolean;
        cb?: () => void;
      }
    | {
        shadowOptions?: unknown;
        decorationOptions?: unknown;
        cb?: () => void;
      };
  [ASSET_ADD]: OnDropValue & {
    asset: LibraryAsset;
  };
  [ASSET_CREATE]: { libraryId: string };
  [ASSET_CREATE_EMPTY]: { libraryId: string };
  [ADD_BASIC_SHAPE]: string;
  [ADD_TEXTURE]: eventTypes.AddTextureEventPayload;
  [ADD_BACKGROUND_IMAGE]: ElementType;
  [ADD_TEMPLATE]: {
    id: string;
    newProject?: boolean;
    selectedUseCase?: string | null | undefined;
    openRelatedView?: boolean;
    callback?: () => void;
  };
  [ADD_TEXT_LAYOUT]: {
    state: {
      objects: SerializedObject[];
      structure: StructureObject[];
    };
    callback?: () => void;
  } & OnDropValue;
  [ADD_PHOTO]: {
    unsplashId: string;
    objectName: string;
    skipPopularityTracking: boolean;
    fireToast: void | ((...args: unknown[]) => void);
    callback: () => void;
    dropX?: number;
    dropY?: number;
    distanceToTopCorner?: {
      x: number;
      y: number;
    };
    imgWidth?: number;
    imgHeight?: number;
  };
  [ADD_MASK]: eventTypes.AddMaskEventPayload;
  [ADD_MOCKUP_TEMPLATE]: string | number;
  [ADD_GENERATED_DESIGN]: {
    designs: AddGeneratedDesign[];
    cb?: (design: Design) => void;
  };
  [EDIT_TEXT]: {
    target: fabric.PathText;
    selectAll?: boolean;
    deselect?: eventTypes.AddTextEvent['val']['deselect'];
    // don't track the edit event separately on an add event
    skipTracking?: boolean;
  };
  [EDIT_TEXTURE]: {
    isChanging?: boolean;
    globalCompositeOperation?: BlendMode;
    opacity?: number;
    callback?: () => void;
  };
  [PREVIEW_TEXTURE_BLEND_MODE]: BlendModePayload;
  [EDIT_BACKGROUND_IMAGE]: { opacity: number; callback?: () => void };
  [PREVIEW_BACKGROUND_BLEND_MODE]: BlendModePayload;
  [ASSET_EDIT]: eventTypes.AssetEditEvent;
  [REPLACE_ELEMENT]: {
    selection?: MinSerializedObject[] | null;
    id?: string | number;
    objectName?: string;
    elementType?: string;
    category?: string | undefined;
    groupName?: string | undefined;
    groups?: { id: number; category?: string; name: string }[] | undefined;
    originPanel?: string | undefined;
    layerName?: string | undefined;
    callback?: () => void;
    replacingElementDoesntHaveToBeSelected?: boolean;
  };
  [ACTIVE_OPACITY]: {
    opacity: number;
    isChanging?: boolean;
    callback?: () => void;
  };
  [ACTIVE_MOVE]: fabric.Event<Event>;
  [ACTIVE_MOVE_BY]: { x: number; y: number };
  [ACTIVE_MOVED]: fabric.Event<Event> | null;
  [ACTIVE_COPY_OBJECTS_AS_STATE]: CopyActiveObjectsAsStateEvent['val'];
  [ACTIVE_PASTE_FROM_CLIPBOARD]: PasteFromClipboardEvent['val'];
  [ACTIVE_COPY_STYLE]: CopyActiveObjectStyleEvent['val'];
  [ACTIVE_COPY_STYLE_PHOTO_FILTERS]: CopyActiveObjectStyleEvent['val'];
  [ACTIVE_COPY_STYLE_FILL]: CopyActiveObjectStyleEvent['val'];
  [ACTIVE_COPY_STYLE_FONT]: CopyActiveObjectStyleEvent['val'];
  [ACTIVE_COPY_STYLE_TEXT_DECORATION]: CopyActiveObjectStyleEvent['val'];
  [ACTIVE_COPY_STYLE_SHADOW]: CopyActiveObjectStyleEvent['val'];
  [ACTIVE_PASTE_STYLE]: PasteActiveObjectStyleEvent['val'];
  [ACTIVE_PASTE_FROM_EVENT]: PasteFromClipboardEvent['val'];
  [ACTIVE_PASTE_FROM_DROP]: PasteFromFileDropEvent['val'];
  [ASSET_CREATE_FROM_GROUP]: never;
  [ASSET_RENAMED]: eventTypes.AssetRenamedEvent;
  [ASSET_DETACH]: { asset: LibraryAsset };
  [ASSETS_UPDATED]: eventTypes.AssetsUpdatedEvent;
  [ACTIVE_CUT]: undefined;
  [ACTIVE_REFLECT_HORIZONTAL]: undefined;
  [ACTIVE_REFLECT_VERTICAL]: undefined;
  [ACTIVE_SAVE_AS_STYLE]: { callback: () => void };
  [ACTIVE_USE_AS_STYLE_SWITCH_IMAGE]: { callback: () => void };
  [ACTIVE_REPLACE_ELEMENT]: { callback: () => void };
  [ACTIVE_ELEMENT_OPACITY_ANIMATE_START]: { id: string | undefined };
  [ACTIVE_ELEMENT_OPACITY_ANIMATE_STOP]: {
    id: string | undefined;
    callback?: () => void;
  };
  [ACTIVE_GROUP]: undefined;
  [ACTIVE_LOCK]: undefined;
  [ACTIVE_ALIGN]: {
    id: Align;
    callback?: ({ alignment }: { alignment?: Align }) => void;
  };
  [ACTIVE_GLYPH]: Glyph;
  [ACTIVE_OVERLAY_MODE]: {
    globalCompositeOperation: string;
    callback?: () => void;
  };
  [PREVIEW_OVERLAY_MODE]: BlendModePayload;
  [ACTIVE_COLOR_HIDE]: { key: string; visibility?: boolean };
  [ACTIVE_STROKE_WIDTH]: (
    | eventTypes.StrokeUpdateOptions
    | PreviewingParams<eventTypes.StrokeUpdateOptions>
  ) & { callback?: () => void };
  [ACTIVE_FONTSIZE]: Partial<CharacterStyles> & { callback?: () => void };
  [ACTIVE_LETTERSPACING]: Partial<CharacterStyles>;
  [ACTIVE_FONT_STYLE_ATTRIBUTES]: TextPresetParameters;
  [ACTIVE_FONTFAMILY]: Partial<CharacterStyles> & { callback?: () => void };
  [ACTIVE_TEXTALIGNMENT]: Partial<CharacterStyles>;
  [ACTIVE_UPPERCASE]: Partial<CharacterStyles>;
  [ACTIVE_LIGATURES]: Partial<CharacterStyles>;
  [ACTIVE_LINEHEIGHT]: Partial<CharacterStyles>;
  [ACTIVE_VARIATION]: {
    variation: FontVariationAxis;
    value: number;
  };
  [ACTIVE_SHADOW]:
    | (eventTypes.ShadingUpdateOptions & {
        callback?: () => void;
      })
    | (PreviewingParams<eventTypes.ShadingUpdateOptions> & {
        callback?: () => void;
      });
  [ACTIVE_TRANSFORM]: Partial<TransformOptions> & { callback?: () => void };
  [ACTIVE_TRANSFORM_CURVE]:
    | (Partial<TransformOptions> & { callback?: () => void })
    | (PreviewingParams<Partial<TransformOptions>> & { callback?: () => void });
  [ACTIVE_DECORATION]:
    | (eventTypes.DecorationUpdateOptions & { callback?: () => void })
    | (PreviewingParams<eventTypes.DecorationUpdateOptions> & {
        callback?: () => void;
      });
  [ACTIVE_VECTORIZE]: {
    colorCount: number;
    solver: VectorizingSolver;
    actionOrigin?: ActionOrigin;
    callback?: () => void;
  };
  [ACTIVE_VECTORIZE_BULK]: {
    colorCount: number;
    solver: VectorizingSolver;
    callback: () => void;
  };
  [ACTIVE_FILTER]: {
    value: SerialisedPhotoFilter[];
    isChanging?: boolean;
    callback?: () => void;
  };
  [ACTIVE_COLOR_PREVIEW]: { key: string; value: ColorOptions['color'] };
  [ACTIVE_AS_BACKGROUND_IMAGE]: undefined;
  [ACTIVE_AS_TEXTURE]: {
    callback?: () => void;
  };
  [ACTIVE_RESIZE]: ResizePayload;
  [ACTIVE_SET_ERASE_STROKE_WIDTH]:
    | number
    | eventTypes.EraserChangeParams
    | PreviewingParams<eventTypes.EraserChangeParams>;
  [ACTIVE_INCREASE_ERASE_STROKE_WIDTH]: { strokeWidthFactor: number };
  [ACTIVE_DECREASE_ERASE_STROKE_WIDTH]: { strokeWidthFactor: number };
  [ACTIVE_SCALING]: fabric.Event<Event>;
  [ACTIVE_SCALED]: fabric.Event<Event>;
  [ACTIVE_REPLACE_TEXT]: { text: string };
  [REPLACE_TEXTS_BULK]: GenerateTextProps[];
  [ACTIVE_NEW_ARTBOARD]: ActiveArtboardEventValue;
  [LAYER_LOCKED]: { id: string };
  [LAYER_HIDDEN]: { id: string; callback: () => void };
  [LAYER_MOVE]: {
    ids: (string | undefined)[];
    targetId: string | undefined;
    relativePosition: number;
    callback: () => void;
  };
  [LAYER_SELECTION]: {
    ids: string[];
  };
  [LAYER_SEND_TO]: {
    sendTo: MoveType;
    trigger?: string;
    callback?: TLatencyActionCompleteCallback;
  };
  [LAYER_RENAME]: {
    layerId: string;
    newLayerName: string;
  };
  [LAYER_EDITING_NAME]: { isEditing: boolean };
  [LAYER_HIGHLIGHT]: { node?: EventDataNode };
  [HISTORY_CHANGES]: eventTypes.HistoryChangesEvent;
  // this is the same event as above, but it's not tracked in the history
  [UNTRACKED_HISTORY_CHANGES]: eventTypes.HistoryChangesEvent;
  [HISTORY_UNDO]: undefined;
  [HISTORY_REDO]: undefined;
  [HISTORY_RESET]: DesignStateMultiBoard;
  [DESIGN_RENAME]: { title: string };
  [DESIGN_SAVE]: eventTypes.DesignSaveEvent;
  [DESIGN_DELETE]: {
    id: string;
  };
  [DESIGN_DUPLICATE]: DuplicateEventValue;
  [DESIGN_SHARE]: {
    callback: (() => void) | ((response: Snapshot) => Promise<void>);
    onCannotSubmit: () => void;
    onError: (error: string) => void;
  };
  [DESIGN_PREVIEW]: PreviewEventValue;
  [DESIGN_RESTORE_LAST_SAVE]: RestoreLastSaveEventValue;
  [UPDATE_PROJECT_UNIT]: Unit;
  [OPEN_DOWNLOAD_PANEL]: undefined;
  [SET_STATE]: {
    state: string | DesignState | HistoryState | DesignStateMultiBoardForInit;
    isHistoryChange?: boolean;
  };
  [SELECT_ALL]: undefined;
  [SET_BACKGROUND]: Partial<ArtboardOptions> & {
    isChanging?: boolean;
    color?: string;
  };
  [SET_COLOR_PALETTE]: {
    toUpdate: ColorPaletteItemLinkedObject[];
    newColor: Color;
    trigger: ColorPaletteTrigger | null;
    isChanging?: boolean;
  };
  [SET_COLOR_PALETTE_PREVIEW]: {
    toUpdate: ColorPaletteItemLinkedObject[];
    newColor: Color;
  };
  [SET_COLOR_PALETTE_PRESET]: {
    callback?: () => void;
    colorPalettePreset: string[];
    colorKeys: string[];
    presetName: string;
    trigger: ColorPaletteTrigger;
  };
  [SET_COLOR_PALETTE_PRESET_PREVIEW]: {
    colorPalettePreset: string[];
    colorKeys: string[];
    presetName: string;
    callback?: () => void;
  };
  [GRID_SET]: {
    gridSize: number;
    isChanging: boolean;
  };
  [GRID_SHOW]: boolean;
  [RULER_SHOW]: boolean;
  [PRINT_MARGINS_SHOW]: boolean;
  [ZOOM]: string | number;
  [ZOOM_TO]: {
    zoom: number;
    pos: { x: number; y: number };
    callback?: () => void;
  };
  [ZOOM_STEP]: number;
  [ENABLE_SNAPPING]: boolean;
  [ENABLE_ZOOM_ON_WHEEL]: boolean;
  [ENABLE_DRAGGING]: boolean;
  [RESIZE]: ArtboardSizeOptions & {
    lockAspectRatio?: number;
    dpi?: number;
    isChanging?: boolean;
  };
  [SOCIAL_SHARE]: { label: ShareType };
  [COLOR_PICKING]: boolean;
  [PICK_COLOR]: {
    pickEvent: MouseEvent;
    color: string | null;
  };
  [DELETE_UPLOAD]: eventTypes.DeleteUploadsEventPayload;
  [SELECT_ARTBOARD]: { board?: StandardBoard; id?: string };
  [POST_SUBMIT]: {
    templateName: string;
    useCases: string[];
    tags: Array<string>;
    description: string;
    premium?: boolean;
    challengeId?: string;
    id?: string;
    category?: string;
    callback: (
      response:
        | Post
        | {
            id?: string;
            challenge?: { slug: string };
            error?: string;
          }
    ) => void;
  };
  [OVERLAY_CLIP]: { renderClip: boolean };
  [DRAGGING_ELEMENT]: eventTypes.DropImageType;
  [REMOVE_OBJECTS]: eventTypes.RemovedObjectsEventPayload;
  [ARTBOARD_CLIP]: {
    objects?: { object: string }[];
    trigger?: string;
  };
  [ARTBOARD_PRINT_MARGIN]: {
    partialPrintMargins: Partial<PrintMargins>;
    trigger: PrintMarginTrigger;
  };
  [UPDATE_PROJECT_PRINT_BLEED]: {
    partialPrintBleed: Partial<PrintBleed>;
    trigger: PrintBleedTrigger;
  };
  [DEV_SAVE_MASTER_LAYOUT]: {
    id: string | boolean | null;
    name: string;
    masterLayoutCategoryId: number;
    callback: (value: TemplateResponse | PromiseLike<TemplateResponse>) => void;
  };
  [DEV_ADJUST_TEXT]: {
    text: string;
  };
  [DEV_REPLICATE_TEXT_IN_ALL_FONTS]: undefined;
  [MTC_SAVE]: {
    callback: () => void;
  };
  [MTC_UPDATE]: {
    callback: () => void;
  };
  [MTC_CREATE_WRAP_GRID]: {
    row: number | undefined;
    col: number | undefined;
  };
  [MAIN_SELECT]: { effects: string[] };
  [SET_ACTIVE_TOOL]: {
    tool: ActiveTool;
  };
  [PREPARE_ARTBOARD_SELECTION]: {
    cb: (artboardSelectionRequired: boolean) => void;
  };
  [PREPARE_ARTBOARD_PRINT_MARGINS]: PrintMargins;
  [PRESET_LINK]: eventTypes.PresetLinkEvent;
  [PRESET_UNLINK]: eventTypes.PresetUnlinkEvent;
  [PRESET_LINK_VIA_COLOR_PALETTE]: eventTypes.PresetLinkEvent;
  [PRESET_UNLINK_VIA_SELECTION]: eventTypes.PresetUnlinkViaSelectionEvent;
  [PRESETS_CREATE]: eventTypes.PresetsCreateEvent;
  [PRESETS_PRE_UPDATE]: eventTypes.PresetsPreUpdateEvent;
  [PRESETS_UPDATE]: eventTypes.PresetsUpdateEvent;
  [PRESETS_REMOVE]: eventTypes.PresetsRemoveEvent;
  [PRESETS_PRE_REMOVE_APPLY]: eventTypes.PresetsPreRemoveApplyEvent;
  [ADD_TEXT_OR_LINK_PRESET_STYLE]: {
    preset: LibraryPreset;
  };
  [FOCUS_MODE_ASCEND]: undefined;
  [FOCUS_MODE_SUBMIT_CHANGES]: undefined;
  [UPDATE_ARTBOARD_DEFAULT_TITLE]: string;
  [ACTIVE_DOWNLOAD]: { isActiveDownload: boolean; trigger?: string };
  [DOWNLOAD_SELECTION]: {
    multiplier: number;
    format: DownloadFormat;
    dpi: number;
    fileName: string;
    removeBackground: boolean | undefined;
    plan: SubscriptionPlan | undefined;
    nodeIds: string[];
    boardId: string | undefined;
    maximums: {
      mm: number;
      in: number;
      px: number;
    };
    artboards?: StandardBoard[];
  }[];
  [DOWNLOAD_WITH_REFACTORING]: eventTypes.DownloadEventPayload;
  [COPY_AS_PNG]: undefined;
  [COPY_LINK_TO_SELECTION]: undefined;
  [ACTIVE_UPDATE_SEGMENTS]: { nPoints: number; isChanging: boolean };
  [COLOR_PAL_GENERATE_COLOR_PALETTE]: { shouldOpenColorPalettePanel: boolean };
  [COLOR_PAL_DELETE_COLOR_PALETTE]: { colorPaletteId: string };
}

// List if prefixes that should reset the current active tool to move mode
export const SHOULD_RESET_ACTIVE_TOOL = [ADD_PREFIX, ASSET_ADD];

export const otherAddRemoveEvents = [
  ACTIVE_COPY_OBJECTS_AS_STATE,
  ACTIVE_PASTE_FROM_CLIPBOARD,
  ACTIVE_PASTE_FROM_EVENT,
  ACTIVE_PASTE_FROM_DROP,
  ACTIVE_CUT,
  ACTIVE_DELETE,
  DELETE_UPLOAD,
  REMOVE_OBJECTS,
  REPLACE_ELEMENT,
  ACTIVE_DUPLICATE,
  ACTIVE_REPLACE_TEXT,
  REPLACE_TEXTS_BULK,
  ACTIVE_NEW_ARTBOARD,
];
